import { api, unwrap } from '../client'
import type { Me, OtpChallenge, OtpVerifyResult, Student, TokenResponse } from '../types'

/* Thin typed wrappers. No logic beyond naming.

   Two things about this backend's auth shape drive the screens above it:

     · EMAIL is the login identifier, not the mobile number. The OTP is keyed
       by (email, purpose) rather than an opaque challenge id.
     · The token response carries NO user. Signing in is therefore two calls —
       obtain the token, then read /auth/me — and `useAuth` owns that pairing
       so no screen has to remember it. */

export type OtpPurpose = 'signup' | 'password_reset'

export const authApi = {
  requestOtp(body: { email: string; purpose: OtpPurpose }): Promise<OtpChallenge> {
    return unwrap(api.POST('/v1/auth/otp/request', { body }))
  },

  verifyOtp(body: { email: string; code: string; purpose: OtpPurpose }): Promise<OtpVerifyResult> {
    return unwrap(api.POST('/v1/auth/otp/verify', { body }))
  },

  /* Completes registration: the OTP's registration_token is exchanged for a
     session, and the profile fields are supplied here rather than up front. */
  setPassword(body: {
    registration_token: string
    name: string
    password: string
    mobile_number?: string | null
    state?: string | null
  }): Promise<TokenResponse> {
    return unwrap(api.POST('/v1/auth/password/set', { body }))
  },

  forgotPassword(body: { email: string }): Promise<{ ok: boolean }> {
    return unwrap(api.POST('/v1/auth/password/forgot', { body }))
  },

  /* Resetting does NOT sign you in — it returns `{ ok }` and the student logs
     in with the new password. That is the backend's choice and the screen
     follows it rather than faking a session. */
  resetPassword(body: { token: string; password: string }): Promise<{ ok: boolean }> {
    return unwrap(api.POST('/v1/auth/password/reset', { body }))
  },

  changePassword(body: { current_password: string; new_password: string }): Promise<{ ok: boolean }> {
    return unwrap(api.POST('/v1/auth/password/change', { body }))
  },

  login(body: { email: string; password: string }): Promise<TokenResponse> {
    return unwrap(api.POST('/v1/auth/login', { body }))
  },

  /* `totp_code` is optional: an admin without 2FA enrolled omits it, and one
     with it enrolled is asked for it after the first attempt comes back
     demanding it. */
  adminLogin(body: {
    email: string
    password: string
    totp_code?: string | null
  }): Promise<TokenResponse> {
    return unwrap(api.POST('/v1/auth/admin/login', { body }))
  },

  me(): Promise<Me> {
    return unwrap(api.GET('/v1/auth/me', {}))
  },

  adminMe(): Promise<Me> {
    return unwrap(api.GET('/v1/auth/admin/me', {}))
  },

  async logout(): Promise<void> {
    await api.POST('/v1/auth/logout', {})
  },

  updateProfile(body: {
    name?: string | null
    mobile_number?: string | null
    state?: string | null
  }): Promise<Student> {
    return unwrap(api.PATCH('/v1/students/me', { body }))
  },
}
